import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import {
  Container,
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import { useUser } from "../UserContext"; // Assuming a useUser hook or context is available
import { db } from "../firebase"; // Firestore instance

const BackgroundBox = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  // background: `linear-gradient(to right, ${theme.palette.error.light}, ${theme.palette.error.dark})`,
  color: theme.palette.common.white,
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: theme.spacing(2),
  boxShadow: theme.shadows[3],
  textAlign: "center",
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    width: "100%",
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  width: "100%",
  padding: theme.spacing(1.5),
}));

function LandingPage() {
  const navigate = useNavigate();
  const { user } = useUser();
  const [showPrices, setShowPrices] = useState(false);
  const [loadingPrices, setLoadingPrices] = useState(false);
  const [prices, setPrices] = useState(null);

  const fetchPrices = async () => {
    setLoadingPrices(true);
    try {
      const docRef = doc(db, "pennantSettings", "settingsDoc");
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setPrices(docSnap.data());
      } else {
        console.error("No pricing data found.");
        setPrices(null);
      }
    } catch (error) {
      console.error("Error fetching pricing data:", error);
      setPrices(null);
    } finally {
      setLoadingPrices(false);
    }
  };

  useEffect(() => {
    if (showPrices) {
      fetchPrices();
    }
  }, [showPrices]);

  const renderPricingContent = () => {
    if (loadingPrices) {
      return <CircularProgress />;
    }

    if (prices) {
      return (
        <>
          <Typography variant="h5" gutterBottom>
            Court Hire Prices
          </Typography>
          <Typography variant="h7">4 people max per Court</Typography>
          
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">Junior</Typography>
              <Typography variant="body1">30 Minutes: ${prices.juniorPrice30}</Typography>
              <Typography variant="body1">60 Minutes: ${prices.juniorPrice60}</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6">Adult - 18+</Typography>
              <Typography variant="body1">30 Minutes: ${prices.adultPrice30}</Typography>
              <Typography variant="body1">60 Minutes: ${prices.adultPrice60}</Typography>
            </Grid>
          </Grid>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h7">Eye protection is recomended for all players but mandatory for anyone under 18 years old.</Typography>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>

<Button
  variant="contained"
  color="error"
  onClick={() => setShowPrices(false)} // Back to main content
>
  Back
</Button>
</Grid>
          <Grid item xs={12} sm={6}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/dashboard/court-bookings")}
          >
            Book Now
          </Button>
          </Grid>
          
          </Grid>
        </>
      );
    }

    return (
      <Typography variant="body2" color="error">
        Unable to load pricing information. Please try again later.
      </Typography>
    );
  };

  return (
    <BackgroundBox>
      <Container maxWidth="sm">
        {showPrices ? (
          <StyledPaper>{renderPricingContent()}</StyledPaper>
        ) : (
          <StyledPaper>
           <Box
  sx={{
    width: "100%",        // Full width
    display: "flex",
    justifyContent: "flex-end",  // Push the button to the right
    mb: 8,                // Bottom margin
  }}
>
  <LoginButton variant="contained" color="primary" onClick={() => navigate("/login")}>
    Member Login
  </LoginButton>
</Box>

            <Typography variant="h4" gutterBottom>
              Welcome to Ballarat Squash & Racquetball
            </Typography>
            <Typography variant="body1" gutterBottom>
              Casual Court Bookings Welcome!
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body1" gutterBottom>
              Book A Court below to have a casual hit, or join as a member to
              access our full range of facilities and pennants!
            </Typography>
            <Divider sx={{ my: 3 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <ActionButton
                  variant="contained"
                  color="primary"
                  startIcon={<SportsTennisIcon />}
                  onClick={() => setShowPrices(true)} // Show prices
                >
                  Book a Court
                </ActionButton>
              </Grid>

              {user && (
                <Grid item xs={12} sm={6}>
                  <ActionButton
                    variant="contained"
                    color="info"
                    startIcon={<CalendarViewMonthIcon />}
                    onClick={() => navigate("/dashboard/my-bookings")}
                  >
                    My Bookings
                  </ActionButton>
                </Grid>
              )}

<Grid item xs={12} sm={6}>
  <ActionButton
    variant="contained"
    color="warning"
    startIcon={<SportsTennisIcon />}
    onClick={() =>
      navigate(
        user
          ? "/dashboard/member-and-pennant-info" // Protected route for logged-in users
          : "/member-pennant-info" // Public route for non-logged-in users
      )
    }
  >
    Member & Pennant Info
  </ActionButton>
</Grid>

              {user && (
                <Grid item xs={12} sm={6}>
                  <ActionButton
                    variant="contained"
                    color="success"
                    startIcon={<AssignmentIndOutlinedIcon />}
                    onClick={() => navigate("/dashboard/membership")}
                  >
                    Join as a Member
                  </ActionButton>
                </Grid>
              )}
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Typography variant="body2" color="textSecondary">
              Find us at 106 Dowling Street, Wendouree VIC 3355
            </Typography>
          </StyledPaper>
        )}
      </Container>
    </BackgroundBox>
  );
}

export default LandingPage;
